import { InvoiceDocument, TAX_CODES } from '../types/invoice'
import { formatAmount } from './currencyFormat'

const TOLERANCE = 0.01

export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
  warnings: ValidationWarning[]
}

export interface ValidationError {
  rule: string
  message: string
  field?: string
}

export interface ValidationWarning {
  rule: string
  message: string
  field?: string
}

// Get tax rate for a given tax code
function getRateForTaxCode(taxCode: string): number {
  const code = TAX_CODES.find((tc) => tc.code === taxCode)
  if (!code) return 0

  // I is 10% GST (inclusive - tax is included in price)
  if (taxCode === 'I') return 0.1

  // E is 0% at line level (exclusive - tax calculated elsewhere, not shown on lines)
  if (taxCode === 'E') return 0

  // Z, R, N are 0%
  return 0
}

// Check if tax code is taxable
function isTaxable(taxCode: string): boolean {
  return taxCode === 'I' // Only "I" is taxable at line level
}

// Check if tax code is zero-rated
function isZeroRated(taxCode: string): boolean {
  return (
    taxCode === 'Z' || taxCode === 'R' || taxCode === 'N' || taxCode === 'E'
  ) // E also requires 0 tax
}

// Validate invoice/credit note
export function validateInvoice(
  document: InvoiceDocument,
  documentType: 'Invoice' | 'Credit Note',
): ValidationResult {
  const errors: ValidationError[] = []
  const warnings: ValidationWarning[] = []
  const currency = document.currency

  // Rule 1: Arithmetic check – Subtotal + Tax must equal Total
  const calculatedTotal = document.subtotal + document.taxAmt
  if (Math.abs(document.total - calculatedTotal) > TOLERANCE) {
    errors.push({
      rule: 'Rule 1: Arithmetic Check',
      message: `Subtotal + Tax must equal Total. Expected ${formatAmount(calculatedTotal, currency)}, got ${formatAmount(document.total, currency)}`,
      field: 'total',
    })
  }

  // Validate each line item's tax code logic
  document.lineItems.forEach((lineItem, index) => {
    const taxCode = lineItem.taxCode
    if (!taxCode) return

    const rate = getRateForTaxCode(taxCode)
    const expectedTax = Math.round(lineItem.subtotal * rate * 100) / 100
    const actualTaxIsZero = Math.abs(lineItem.taxAmt) <= TOLERANCE
    const subtotalIsZero = Math.abs(lineItem.subtotal) <= TOLERANCE

    // CRITICAL RULE: Tax code "E" (Exclusive) MUST have $0.00 tax at line level
    if (taxCode === 'E' && !actualTaxIsZero) {
      errors.push({
        rule: 'Tax Code E Rule',
        message: `Line ${index + 1}: Tax code "E" (Exclusive) means tax is NOT shown at line level. Tax amount must be ${formatAmount(0, currency)}, got ${formatAmount(lineItem.taxAmt, currency)}`,
        field: 'taxAmt',
      })
    }

    // Rule 2: Tax-from-rate check - Tax must match the calculated tax from the rate
    if (Math.abs(lineItem.taxAmt - expectedTax) > TOLERANCE) {
      errors.push({
        rule: 'Rule 2: Tax Rate Check',
        message: `Line ${index + 1}: Tax does not match rate for tax code "${taxCode}". Expected ${formatAmount(expectedTax, currency)}, got ${formatAmount(lineItem.taxAmt, currency)}`,
        field: 'taxAmt',
      })
    }

    // Rule 3 & 7 COMBINED: Taxable codes (I only) MUST have tax (unless subtotal is zero)
    if (isTaxable(taxCode) && actualTaxIsZero && !subtotalIsZero) {
      errors.push({
        rule: 'Rule 3/7: Tax Required',
        message: `Line ${index + 1}: Tax code "${taxCode}" is taxable (10% GST). Tax amount cannot be zero when subtotal is ${formatAmount(lineItem.subtotal, currency)}. Expected tax: ${formatAmount(expectedTax, currency)}`,
        field: 'taxCode',
      })
    }

    // Rule 4 & 8 COMBINED: Zero-rated codes (Z/R/N/E) MUST have zero tax
    if (isZeroRated(taxCode) && !actualTaxIsZero) {
      errors.push({
        rule: 'Rule 4/8: Invalid Tax Amount',
        message: `Line ${index + 1}: Tax code "${taxCode}" requires zero tax at line level. Tax amount must be ${formatAmount(0, currency)}, but got ${formatAmount(lineItem.taxAmt, currency)}`,
        field: 'taxCode',
      })
    }

    // Additional check: If subtotal is zero, tax should also be zero regardless of code
    if (subtotalIsZero && !actualTaxIsZero) {
      errors.push({
        rule: 'Zero Subtotal Check',
        message: `Line ${index + 1}: Subtotal is zero, so tax amount must also be zero. Got ${formatAmount(lineItem.taxAmt, currency)}`,
        field: 'taxAmt',
      })
    }
  })

  // Invoice-level validation: Check if invoice totals match sum of line items
  const lineItemsSubtotal = document.lineItems.reduce(
    (sum, item) => sum + item.subtotal,
    0,
  )
  const lineItemsTax = document.lineItems.reduce(
    (sum, item) => sum + item.taxAmt,
    0,
  )
  const lineItemsTotal = document.lineItems.reduce(
    (sum, item) => sum + item.total,
    0,
  )

  if (Math.abs(document.subtotal - lineItemsSubtotal) > TOLERANCE) {
    errors.push({
      rule: 'Invoice Totals Check',
      message: `Invoice subtotal (${formatAmount(document.subtotal, currency)}) does not match sum of line items (${formatAmount(lineItemsSubtotal, currency)})`,
      field: 'subtotal',
    })
  }

  if (Math.abs(document.taxAmt - lineItemsTax) > TOLERANCE) {
    errors.push({
      rule: 'Invoice Totals Check',
      message: `Invoice tax amount (${formatAmount(document.taxAmt, currency)}) does not match sum of line items (${formatAmount(lineItemsTax, currency)})`,
      field: 'taxAmt',
    })
  }

  if (Math.abs(document.total - lineItemsTotal) > TOLERANCE) {
    errors.push({
      rule: 'Invoice Totals Check',
      message: `Invoice total (${formatAmount(document.total, currency)}) does not match sum of line items (${formatAmount(lineItemsTotal, currency)})`,
      field: 'total',
    })
  }

  // Rule 5: Credit note sign consistency
  if (documentType === 'Credit Note') {
    const hasInconsistentSigns = !(
      document.subtotal <= 0 &&
      document.taxAmt <= 0 &&
      document.total <= 0
    )

    if (hasInconsistentSigns) {
      warnings.push({
        rule: 'Rule 5: Credit Note Signs',
        message:
          'Credit note amounts should all be negative (Subtotal, Tax, and Total)',
        field: 'amounts',
      })
    }
  }

  // Rule 6: Invoice sign sanity check
  if (documentType === 'Invoice') {
    const hasNegativeAmounts =
      document.subtotal < 0 || document.taxAmt < 0 || document.total < 0

    if (hasNegativeAmounts) {
      warnings.push({
        rule: 'Rule 6: Invoice Signs',
        message:
          'Invoice amounts should be positive (Subtotal, Tax, and Total)',
        field: 'amounts',
      })
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  }
}
